import { useState } from "react";
import { Star, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { Card, SectionTitle } from "@/components/common";

export function Stars({ value = 0, onPick, size = "h-5 w-5", testid = "stars" }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onPick} onClick={() => onPick?.(n)} className={onPick ? "hover:scale-110 transition-transform" : "cursor-default"} data-testid={`${testid}-${n}`} aria-label={`${n}`}>
          <Star className={`${size} ${n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </span>
  );
}

export function RatingBadge({ c }) {
  const { t } = useI18n();
  if (!c?.rating_count) return <span className="text-xs text-slate-400" data-testid="rating-badge">★ {t("rt.none")}</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="rating-badge"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{c.rating_avg.toFixed(1)} ({c.rating_count})</span>;
}

export function RateVisit({ r, reload }) {
  const { t } = useI18n();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (!["completed", "closed"].includes(r.status) || !r.contractor) return null;
  const send = async () => {
    setBusy(true);
    try { await api.post(`/requests/${r.id}/rate`, { stars, comment }); toast.success(t("rt.thanks")); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Card data-testid="rate-visit-card">
      <SectionTitle>{t(r.rating ? "rt.your" : "rt.title")}</SectionTitle>
      {r.rating ? (
        <div><Stars value={r.rating.stars} testid="rated-stars" />{r.rating.comment && <p className="text-sm text-slate-600 mt-2">{r.rating.comment}</p>}</div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">{t("rt.d")}</p>
          <Stars value={stars} onPick={setStars} size="h-8 w-8" testid="rate-stars" />
          <Textarea rows={2} placeholder={t("rt.comment")} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="rate-comment-input" />
          <Button className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" disabled={!stars || busy} onClick={send} data-testid="rate-submit-btn">{t("rt.send")}</Button>
        </div>
      )}
    </Card>
  );
}

export function RepeatCleaning({ r, reload }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const rec = r.cleaning?.recurrence;
  if (r.status === "customer_canceled") return null;
  const go = async (mode) => {
    setBusy(true);
    try {
      const x = await api.post(`/cleanings/${r.id}/repeat`, { mode });
      if (mode === "once") { toast.success(t("rp.booked", { n: x.data.number })); nav(`/dashboard/requests/${x.data.id}`); } else { toast.success(t("rp.saved")); reload(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const btn = (on) => `rounded-full ${on ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`;
  return (
    <div className="mt-5 border-t pt-4" data-testid="repeat-cleaning">
      <p className="font-semibold text-sm flex items-center gap-2"><Repeat className="h-4 w-4 text-blue-600" />{t("rp.title")}</p>
      <p className="text-xs text-slate-500 mt-1">{t("rp.d")}</p>
      {rec && <p className="mt-2 text-xs font-semibold text-blue-700" data-testid="repeat-status">{t(`rp.on_${rec}`)}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => go("once")} data-testid="repeat-once-btn">{t("rp.once")}</Button>
        <Button size="sm" variant={rec === "weekly" ? "default" : "outline"} className={btn(rec === "weekly")} disabled={busy} onClick={() => go("weekly")} data-testid="repeat-weekly-btn">{t("rp.weekly")}</Button>
        <Button size="sm" variant={rec === "biweekly" ? "default" : "outline"} className={btn(rec === "biweekly")} disabled={busy} onClick={() => go("biweekly")} data-testid="repeat-biweekly-btn">{t("rp.biweekly")}</Button>
        {rec && <Button size="sm" variant="ghost" className="rounded-full text-red-600" disabled={busy} onClick={() => go("stop")} data-testid="repeat-stop-btn">{t("rp.stop")}</Button>}
      </div>
    </div>
  );
}
